import { AsyncPipe } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionsService } from '../../../permissions/permissions.service';
import { SamplesService } from '../../../samples/service/samples.service';
import type { TowRouteData } from '../../interface/tow-route-data.interface';

/**
 * A button that triggers cutting point and sample generation for the current tow.
 * Disabled with a descriptive tooltip when conditions aren't met (no tow log, already generated,
 * or insufficient permissions). Clearing is handled separately via the tow overview actions menu.
 */
@Component({
  standalone: true,
  imports: [AsyncPipe, MatButton, MatTooltip],
  selector: 'cpr-sample-generation-button',
  template: `
    @if (!routeData.samples().length) {
      <button
        mat-flat-button
        [disabled]="disabledReason() | async"
        [disabledInteractive]="disabledReason() | async"
        [matTooltip]="disabledReason() | async"
        (click)="onClick()"
      >
        Generate cutting points and samples
      </button>
    }
  `,
})
export class SampleGenerationButtonComponent {
  private readonly permissionsService = inject(PermissionsService);
  private readonly router = inject(Router);
  private readonly samplesService = inject(SamplesService);

  protected readonly routeData = inject(ActivatedRoute).snapshot.data as TowRouteData;

  protected readonly disabledReason = computed<Promise<string | undefined>>(async () => {
    const towLog = this.routeData.towLog();

    if (!towLog?.length) {
      return 'Tow log has not been entered.';
    }

    if (!(await this.permissionsService.hasPermission('sample', 'generate'))) {
      return 'You lack the sample generate permission.';
    }

    return undefined;
  });

  async onClick() {
    if (await this.disabledReason()) {
      return;
    }

    const { _id } = this.routeData.tow();
    return this.samplesService.generateWithDialog(_id, async () => {
      await this.routeData.samples.refresh();
      await this.router.navigate(['/tows', _id, 'samples', 'allocate']);
    });
  }
}
